import { useState, useRef, useEffect, useCallback } from 'react';
import { useDragResize } from '../../../shared/hooks/useDragResize';
import { DAY_WIDTH, CANVAS_HEIGHT, clampScale } from '../lib/flowCanvas';

// Motor de mouse/touch/pan/zoom/pinch/drag/resize/connect del canvas de roadmap
// (`ComponentTaskFlow.jsx`) -- mismo problema exacto que `Map.jsx` ya resolvió con
// `useMapCanvasInput` (`features/map/hooks/useMapCanvasInput.js`), mismo patrón: recibe los
// valores/refs que el container ya posee (datos de tareas/nodos, callbacks de persistencia) y
// devuelve el estado + los handlers que la vista necesita. A diferencia de Map (que dibuja en
// `<canvas>` y guarda casi todo en refs), Flow renderiza vía JSX, así que buena parte de lo que
// en Map era un ref acá es `useState` real -- se devuelve tal cual para que el container lo siga
// usando en su render.
//
// `flowNodes`/`setFlowNodes` y `drawingLineRef`/`setDrawingLine`/`setLiveMousePoint` siguen
// siendo dueños del container (son datos de nodo / flujo de creación de línea, no el motor de
// interacción) -- se reciben acá solo porque `onFlowMouseDown/Move/Up` y el drag de grupo los
// necesitan tocar. `startConnect` se mueve verbatim pese a no tener ningún caller hoy (ver
// `02.1-tasks.md` -- el mismo hallazgo ya documentado en el container antes de este paso).
export function useFlowCanvasInteraction({
    spaceId,
    tasks,
    mapTasks,
    startDateObj,
    CANVAS_WIDTH,
    toast,
    onTaskDrop,
    onTaskClick,
    onTaskResize,
    flowNodes,
    setFlowNodes,
    setFlowEdges,
    refs: {
        flowContainerRef,
        drawingLineRef,
        mousePosRef,
    },
    setDrawingLine,
    setLiveMousePoint,
}) {
    // Calculate initial position based on today
    const initialFlowPos = useState(() => {
        const today = new Date();
        const fractionOfYear = (today - startDateObj) / (1000 * 60 * 60 * 24);
        const xPos = fractionOfYear * DAY_WIDTH;
        return { x: -xPos + 600, y: -100 };
    })[0];

    const [flowPos, setFlowPos] = useState(initialFlowPos);
    const [flowScale, setFlowScale] = useState(1);
    const flowDragRef = useRef(null);
    const flowPinchRef = useRef(null);

    const [resizingTaskId, setResizingTaskId] = useState(null);
    const [resizeWidth, setResizeWidth] = useState(0);

    const [flowResizingNode, setFlowResizingNode] = useState(null);
    const [isPanMode, setIsPanMode] = useState(false);
    const [selectedNodeIds, setSelectedNodeIds] = useState([]);
    const [selectionBox, setSelectionBox] = useState(null);
    const [flowDrawingEdge, setFlowDrawingEdge] = useState(null);

    const [groupMove, setGroupMove] = useState(null); // { isDragging: boolean, initialPositions: { [id]: { type, x, y, task? } }, dx: 0, dy: 0 }
    const [dropTooltip, setDropTooltip] = useState(null);

    // Hook compartido en vez del patrón mousedown→window.addEventListener('mousemove'/'mouseup')
    // que dejaba listeners colgados si el mouse se soltaba fuera del viewport. La persistencia
    // real la hace `ComponentTaskHoy` vía el callback `onTaskResize` (paso a2b — antes era un
    // `window` CustomEvent `roadmap-resize-task`). `flowScale` se congela en `initialValue` al
    // iniciar el arrastre (no se lee en vivo por movimiento).
    const startTaskResize = useDragResize({
        onMove: ({ startX, initialValue, moveEvent }) => {
            const { currentWidth, scale } = initialValue;
            const dx = (moveEvent.clientX - startX) / scale;
            setResizeWidth(Math.max(12, currentWidth + dx));
            return { ...initialValue, dx };
        },
        onEnd: (finalValue) => {
            const { task, dx = 0 } = finalValue;
            const daysAdded = dx / DAY_WIDTH;

            if (Math.abs(daysAdded) > 0.1) {
                const tStart = task.start_time ? new Date(task.start_time) : new Date(task.date);
                const initialDuration = task.end_time ? (new Date(task.end_time).getTime() - tStart.getTime()) : (24 * 60 * 60 * 1000);
                const newDurationMs = initialDuration + (daysAdded * 24 * 60 * 60 * 1000);
                const newEndMs = tStart.getTime() + Math.max(12 * 60 * 60 * 1000, newDurationMs);
                const newEndDate = new Date(newEndMs);
                onTaskResize?.({ task, newEndDate });
            }

            setResizingTaskId(null);
        },
    });

    const handleResizeStart = (e, task, currentWidth) => {
        e.stopPropagation();
        setResizingTaskId(task.id);
        setResizeWidth(currentWidth);
        startTaskResize(e, { task, currentWidth, scale: flowScale });
    };

    const startGroupMove = useDragResize({
        onMove: ({ startX, startY, initialValue, moveEvent }) => {
            const { scale } = initialValue;
            let dx = (moveEvent.clientX - startX) / scale;
            const dy = (moveEvent.clientY - startY) / scale;

            if (moveEvent.metaKey || moveEvent.ctrlKey) {
                dx = 0;
            }

            setGroupMove(prev => {
                if (!prev) return { isDragging: true, initialPositions: initialValue.initialPositions, dx, dy };
                return { ...prev, dx, dy };
            });
            return { ...initialValue, dx, dy };
        },
        onEnd: (finalValue) => {
            const { initialPositions, clickedElementId, dx = 0, dy = 0 } = finalValue;

            if (Math.abs(dx) < 2 && Math.abs(dy) < 2) {
                if (initialPositions[clickedElementId]?.type === 'task') {
                    if (onTaskClick) onTaskClick(initialPositions[clickedElementId].task);
                }
                setGroupMove(null);
                return;
            }

            // Apply new positions to backend
            Object.entries(initialPositions).forEach(([id, initPos]) => {
                if (initPos.type === 'task') {
                    const projectedX = initPos.x + dx;
                    const daysFromStart = projectedX / DAY_WIDTH;
                    const newDate = new Date(startDateObj.getTime() + daysFromStart * 24 * 60 * 60 * 1000);
                    const newY = initPos.y + dy;

                    let newEndDate = null;
                    const task = initPos.task;
                    if (task.end_time) {
                        const oldStart = new Date(task.start_time || task.date);
                        const oldEnd = new Date(task.end_time);
                        if (!isNaN(oldStart.getTime()) && !isNaN(oldEnd.getTime())) {
                            const duration = oldEnd.getTime() - oldStart.getTime();
                            newEndDate = new Date(newDate.getTime() + duration);
                        }
                    }

                    onTaskDrop?.({ task, newDate, newY, newEndDate });

                    if (id === clickedElementId) {
                        setDropTooltip({ id: task.id, dateObj: newDate });
                        setTimeout(() => {
                            setDropTooltip(prev => prev?.id === task.id ? null : prev);
                        }, 3000);
                    }
                } else if (initPos.type === 'node') {
                    if (!spaceId) return;
                    const newX = initPos.x + dx;
                    const newY = initPos.y + dy;
                    fetch(`/api/roadmap_nodes/${id}`, {
                        method: 'PATCH',
                        headers: { 'Content-Type': 'application/json' },
                        credentials: 'include',
                        body: JSON.stringify({ pos_x: newX, pos_y: newY })
                    }).catch(console.error);

                    // Update node locally
                    setFlowNodes(prev => prev.map(n => n.id === id ? { ...n, x: newX, y: newY } : n));
                }
            });

            setGroupMove(null);
        }
    });

    const handleElementMoveStart = (e, elementId) => {
        e.stopPropagation();

        // Clear selection if clicking an unselected element
        let currentSelection = selectedNodeIds;
        if (!currentSelection.includes(elementId)) {
            currentSelection = [elementId];
            setSelectedNodeIds(currentSelection);
        }

        // Build initialPositions map
        const initialPositions = {};

        currentSelection.forEach(id => {
            const node = flowNodes.find(n => n.id === id);
            if (node) {
                initialPositions[id] = { type: 'node', x: node.x, y: node.y };
            } else {
                const task = tasks.find(t => t.id === id);
                if (task && (task.date || task.start_time)) {
                    const tStart = task.start_time ? new Date(task.start_time) : new Date(task.date);
                    const startFraction = (tStart - startDateObj) / (1000 * 60 * 60 * 24);
                    const startX = startFraction * DAY_WIDTH;
                    const idx = mapTasks.indexOf(task);
                    const oY = (task.roadmap_y != null) ? Number(task.roadmap_y) : (100 + (idx * 60) % 800);
                    initialPositions[id] = { type: 'task', x: startX, y: oY, task };
                }
            }
        });

        setGroupMove({ isDragging: true, initialPositions, dx: 0, dy: 0 });
        startGroupMove(e, { initialPositions, clickedElementId: elementId, scale: flowScale });
    };

    const clampPosition = useCallback((pos, scale) => {
        if (!flowContainerRef.current) return pos;
        const rect = flowContainerRef.current.getBoundingClientRect();
        const maxX = 300;
        const minX = Math.min(maxX, rect.width - (CANVAS_WIDTH * scale) - 300);
        const maxY = 300;
        const minY = Math.min(maxY, rect.height - (CANVAS_HEIGHT * scale) - 300);
        return {
            x: Math.max(minX, Math.min(maxX, pos.x)),
            y: Math.max(minY, Math.min(maxY, pos.y))
        };
    }, [CANVAS_WIDTH, flowContainerRef]);

    // Wheel to pan/zoom
    const onFlowWheel = useCallback((e) => {
        e.preventDefault();
        const rect = flowContainerRef.current.getBoundingClientRect();
        const mouseX = e.clientX - rect.left;
        const mouseY = e.clientY - rect.top;

        if (e.ctrlKey) {
            // pinch gesture on trackpad / mouse wheel + ctrl
            const delta = -e.deltaY * 0.01;
            setFlowScale(prev => {
                const next = clampScale(prev + delta * prev);
                const ratio = next / prev;
                setFlowPos(p => clampPosition({
                    x: mouseX - ratio * (mouseX - p.x),
                    y: mouseY - ratio * (mouseY - p.y),
                }, next));
                return next;
            });
        } else {
            // pan
            setFlowPos(p => clampPosition({
                x: p.x - e.deltaX,
                y: p.y - e.deltaY
            }, flowScale));
        }
    }, [clampPosition, flowScale, flowContainerRef]);

    useEffect(() => {
        const el = flowContainerRef.current;
        if (!el) return;
        el.addEventListener('wheel', onFlowWheel, { passive: false });
        return () => el.removeEventListener('wheel', onFlowWheel);
    }, [onFlowWheel, flowContainerRef]);

    const startNodeResize = (e, nodeId) => {
        e.stopPropagation();
        e.preventDefault();
        const node = flowNodes.find(n => n.id === nodeId);
        if (!node) return;
        setFlowResizingNode({ id: nodeId, startX: e.clientX, startY: e.clientY, origW: node.w || 260, origH: node.h || 180 });
    };
    const startConnect = (e, nodeId, type) => {
        e.stopPropagation();
        e.preventDefault();
        const node = flowNodes.find(n => n.id === nodeId);
        if (!node) return;
        const rect = flowContainerRef.current?.getBoundingClientRect();
        const startX = (e.clientX - rect.left - flowPos.x) / flowScale;
        const startY = (e.clientY - rect.top - flowPos.y) / flowScale;
        setFlowDrawingEdge({ sourceId: nodeId, type, endX: startX, endY: startY });
    };
    const endConnect = (e, targetId, type) => {
        e.stopPropagation();
        if (flowDrawingEdge && flowDrawingEdge.sourceId !== targetId) {
            const newEdge = { id: `edge_${Date.now()}`, sourceId: flowDrawingEdge.sourceId, targetId };
            setFlowEdges(prev => [...prev, newEdge]);
        }
        setFlowDrawingEdge(null);
        setSelectionBox(null);
        setSelectionBox(null);
    };

    const onFlowMouseDown = (e) => {
        if (e.target.closest('.roadmap-task-node, .op-fn-trigger, .op-etapa-node, .op-task-flow-gantt-header')) return;
        if (e.button !== 0) return;

        if (drawingLineRef.current) {
            const rect = flowContainerRef.current?.getBoundingClientRect();
            const x = (e.clientX - rect.left - flowPos.x) / flowScale;
            const y = (e.clientY - rect.top - flowPos.y) / flowScale;
            setDrawingLine(prev => ({ ...prev, points: [...prev.points, { x, y }] }));
            return;
        }

        if (isPanMode) {
            flowDragRef.current = { startX: e.clientX, startY: e.clientY, originX: flowPos.x, originY: flowPos.y };
        } else {
            setSelectedNodeIds([]);
            const rect = flowContainerRef.current?.getBoundingClientRect();
            const x = (e.clientX - rect.left - flowPos.x) / flowScale;
            const y = (e.clientY - rect.top - flowPos.y) / flowScale;
            setSelectionBox({ startX: x, startY: y, currentX: x, currentY: y });
        }
    };
    const onFlowMouseMove = (e) => {
        const rect = flowContainerRef.current?.getBoundingClientRect();
        if (rect) {
            mousePosRef.current = {
                x: (e.clientX - rect.left - flowPos.x) / flowScale,
                y: (e.clientY - rect.top - flowPos.y) / flowScale
            };
        }

        if (drawingLineRef.current) {
            setLiveMousePoint(mousePosRef.current);
            return;
        }

        if (selectionBox) {
            const rect = flowContainerRef.current?.getBoundingClientRect();
            const x = (e.clientX - rect.left - flowPos.x) / flowScale;
            const y = (e.clientY - rect.top - flowPos.y) / flowScale;
            setSelectionBox(prev => ({ ...prev, currentX: x, currentY: y }));

            const minX = Math.min(selectionBox.startX, x);
            const maxX = Math.max(selectionBox.startX, x);
            const minY = Math.min(selectionBox.startY, y);
            const maxY = Math.max(selectionBox.startY, y);

            const newSelectedIds = flowNodes.filter(n => {
                let nw = n.w || 200;
                let nh = n.h || 200;
                if (n.type === 'trigger') { nw = 220; nh = 40; }
                if (n.type === 'etapa') { nw = 220; nh = 100; }
                if (n.type === 'image') { nw = n.w || 200; nh = n.h || 200; }
                return !(n.x > maxX || n.x + nw < minX || n.y > maxY || n.y + nh < minY);
            }).map(n => n.id);

            // Also check tasks if they are mixed in? Yes, tasks are in flowNodes too.
            // Wait, tasks are passed via props `tasks` but we combine them into flowNodes?
            // No, tasks are rendered separately from `tasks` array but they have positions.
            // Let's look at how tasks are rendered.
            // Actually, in the current code, tasks are NOT in flowNodes. flowNodes is only triggers, etapas, images.
            // We need to also select tasks!
            const newTaskIds = mapTasks.filter((t, idx) => {
                const tStart = t.start_time ? new Date(t.start_time) : new Date(t.date);
                if (isNaN(tStart.getTime())) return false;
                const startFraction = (tStart - startDateObj) / (1000 * 60 * 60 * 24);
                const nx = startFraction * 200; // DAY_WIDTH
                const ny = (t.roadmap_y != null) ? Number(t.roadmap_y) : (100 + (idx * 60) % 800);
                const nw = 100; const nh = 32; // approximate min-width task size
                return !(nx > maxX || nx + nw < minX || ny > maxY || ny + nh < minY);
            }).map(t => t.id);

            setSelectedNodeIds([...newSelectedIds, ...newTaskIds]);
            return;
        }
        if (flowResizingNode) {
            const dx = (e.clientX - flowResizingNode.startX) / flowScale;
            const dy = (e.clientY - flowResizingNode.startY) / flowScale;
            setFlowNodes(prev => prev.map(n =>
                n.id === flowResizingNode.id ? { ...n, w: Math.max(20, flowResizingNode.origW + dx), h: Math.max(20, flowResizingNode.origH + dy) } : n
            ));
            return;
        }
        if (flowDrawingEdge) {
            const rect = flowContainerRef.current?.getBoundingClientRect();
            const x = (e.clientX - rect.left - flowPos.x) / flowScale;
            const y = (e.clientY - rect.top - flowPos.y) / flowScale;
            setFlowDrawingEdge(prev => ({ ...prev, endX: x, endY: y }));
            return;
        }
        if (!flowDragRef.current) return;
        const dx = e.clientX - flowDragRef.current.startX;
        const dy = e.clientY - flowDragRef.current.startY;
        setFlowPos(clampPosition({ x: flowDragRef.current.originX + dx, y: flowDragRef.current.originY + dy }, flowScale));
    };
    const onFlowMouseUp = () => {
        flowDragRef.current = null;

        if (flowResizingNode) {
            // We use functional state update to access the latest flowNodes reliably
            setFlowNodes(currentNodes => {
                const node = currentNodes.find(n => n.id === flowResizingNode.id);
                if (node) {
                    fetch(`/api/roadmap_nodes/${node.id}`, {
                        method: 'PATCH',
                        headers: { 'Content-Type': 'application/json' },
                        credentials: 'include',
                        body: JSON.stringify({ width: node.w, height: node.h })
                    }).catch(console.error);
                }
                return currentNodes;
            });
        }

        setFlowResizingNode(null);
        setFlowDrawingEdge(null);
        setSelectionBox(null);
    };
    const onFlowMouseLeave = () => {
        if (flowDragRef.current) {
            flowDragRef.current = null;
        }
        setFlowResizingNode(null);
        setFlowDrawingEdge(null);
        setSelectionBox(null);
    };

    // Touch / pinch support
    const getDistance = (t1, t2) => Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY);
    const onFlowTouchStart = (e) => {
        if (e.target.closest('.roadmap-task-node, .op-fn-trigger, .op-etapa-node, .op-task-flow-gantt-header')) return;
        if (e.touches.length === 2) {
            flowPinchRef.current = { dist: getDistance(e.touches[0], e.touches[1]), scale: flowScale };
        } else if (e.touches.length === 1) {
            flowDragRef.current = { startX: e.touches[0].clientX, startY: e.touches[0].clientY, originX: flowPos.x, originY: flowPos.y };
        }
    };
    const onFlowTouchMove = (e) => {
        e.preventDefault();
        if (e.touches.length === 2 && flowPinchRef.current) {
            const newDist = getDistance(e.touches[0], e.touches[1]);
            const ratio = newDist / flowPinchRef.current.dist;
            const next = clampScale(flowPinchRef.current.scale * ratio);
            setFlowScale(next);
        } else if (e.touches.length === 1 && flowDragRef.current) {
            const dx = e.touches[0].clientX - flowDragRef.current.startX;
            const dy = e.touches[0].clientY - flowDragRef.current.startY;
            setFlowPos(clampPosition({ x: flowDragRef.current.originX + dx, y: flowDragRef.current.originY + dy }, flowScale));
        }
    };
    const onFlowTouchEnd = () => {
        flowDragRef.current = null;
        flowPinchRef.current = null;
    };

    const onFlowDragOver = (e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'copy';
    };

    const onFlowDrop = (e) => {
        e.preventDefault();
        if (!spaceId) {
            toast.error('Debes seleccionar un espacio primero');
            return;
        }
        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            const file = e.dataTransfer.files[0];
            if (file.type.startsWith('image/')) {
                const rect = flowContainerRef.current?.getBoundingClientRect();
                const dropX = (e.clientX - rect.left - flowPos.x) / flowScale;
                const dropY = (e.clientY - rect.top - flowPos.y) / flowScale;

                const compressImage = (fileToCompress) => {
                    return new Promise((resolve, reject) => {
                        const r = new FileReader();
                        r.readAsDataURL(fileToCompress);
                        r.onload = ev => {
                            const img = new Image();
                            img.src = ev.target.result;
                            img.onload = () => {
                                let w = img.width;
                                let h = img.height;
                                if (w > 1200) {
                                    h = Math.round((h * 1200) / w);
                                    w = 1200;
                                }
                                const canvas = document.createElement('canvas');
                                canvas.width = w; canvas.height = h;
                                canvas.getContext('2d').drawImage(img, 0, 0, w, h);
                                canvas.toBlob(blob => {
                                    resolve(new File([blob], fileToCompress.name, { type: 'image/jpeg' }));
                                }, 'image/jpeg', 0.8);
                            };
                            img.onerror = reject;
                        };
                        r.onerror = reject;
                    });
                };

                toast.info('Comprimiendo y subiendo imagen...');
                compressImage(file).then(compressedFile => {
                    return fetch('/api/upload/request', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        credentials: 'include',
                        body: JSON.stringify({ type: 'roadmap_node', filename: compressedFile.name, contentType: compressedFile.type })
                    }).then(r => r.json()).then(resUrl => {
                        if (!resUrl.success) throw new Error(resUrl.error);

                        const formData = new FormData();
                        Object.entries(resUrl.fields).forEach(([k, v]) => formData.append(k, v));
                        formData.append('file', compressedFile);

                        return fetch(resUrl.url, { method: 'POST', body: formData }).then(gcsRes => {
                            if (!gcsRes.ok) throw new Error('Error subiendo a Google Cloud');
                            // Confirmar: verifica el objeto contra el bucket y le fija el Cache-Control.
                            // Devuelve la URL final resuelta (CDN o GCS según la config del backend).
                            return fetch('/api/upload/confirm', {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json' },
                                credentials: 'include',
                                body: JSON.stringify({ path: resUrl.path, type: 'roadmap_node', contentType: compressedFile.type }),
                            }).then(r => r.json()).then((confirmRes) => {
                                if (!confirmRes.success) throw new Error(confirmRes.error || 'Error confirmando la subida');
                                return confirmRes.url;
                            });
                        });
                    }).then(publicUrl => {
                        const newNode = {
                            type: 'image',
                            pos_x: dropX, pos_y: dropY, width: 200, height: 200, content: publicUrl
                        };

                        return fetch(`/api/spaces/${spaceId}/roadmap_nodes`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            credentials: 'include',
                            body: JSON.stringify(newNode)
                        }).then(r => r.json()).then((savedNode) => {
                            setFlowNodes(prev => [...prev, {
                                ...savedNode,
                                id: savedNode.id,
                                type: savedNode.type,
                                x: savedNode.pos_x,
                                y: savedNode.pos_y,
                                w: savedNode.width,
                                h: savedNode.height,
                                src: savedNode.content
                            }]);
                            toast.success('Imagen subida con éxito');
                        });
                    }).catch(err => {
                        console.error(err);
                        toast.error('Error al subir la imagen');
                    });
                });
            }
        }
    };

    return {
        flowPos, setFlowPos, flowScale, setFlowScale,
        isPanMode, setIsPanMode,
        selectedNodeIds, setSelectedNodeIds,
        selectionBox,
        groupMove,
        dropTooltip,
        flowDrawingEdge, setFlowDrawingEdge,
        resizingTaskId, resizeWidth,
        flowDragRef,
        clampPosition,
        handleElementMoveStart, handleResizeStart, startNodeResize, endConnect,
        onFlowMouseDown, onFlowMouseMove, onFlowMouseUp, onFlowMouseLeave,
        onFlowTouchStart, onFlowTouchMove, onFlowTouchEnd,
        onFlowDragOver, onFlowDrop,
    };
}
